import { useMemo } from "react";

import { useSampleAccountSession } from "@/features/sampleAccount/useSampleAccountSession";
import { CLERK_ORGANIZATION_ROLES } from "@shared/constants";
import { isValidOrgType, ORG_TYPE } from "@shared/constants/orgTypes";

import { useAuth, useOrganization } from "@clerk/react";
import { useUser } from "@clerk/react";
import { type UserResource } from "@clerk/shared/types";

import { useSfsUser } from "./useSfsUser";

/**
 * Resolves the current user's effective role for routing and access control.
 * - SFS admin: has org SFS feature
 * - Auditor: org:auditor in current org (restricted: assignments only, no performance/published)
 * - Consultant manager: org:hs_consultant_manager (SFS manage-audit)
 * - Client: org:owner / org:admin / org:client_admin, org:member / org:client_user, or org:viewer in client org (no SFS, not auditor)
 * - Sample guest (ASA-1594): treated as a client member of the shared sample org
 */
export function useUserRole() {
  const { user } = useUser();
  const { has, orgRole: sessionOrgRole } = useAuth();
  const { organization, membership } = useOrganization();
  const { isUser: isSfsUser } = useSfsUser(user as UserResource);
  const { isSampleGuest } = useSampleAccountSession();

  return useMemo(() => {
    if (isSampleGuest) {
      return {
        isSfsAdmin: false,
        isAuditor: false,
        isConsultantManager: false,
        isManager: false,
        isClient: true,
        isClientAdmin: false,
        isSiteManager: false,
        isAreaManager: false,
        isAuditorInClientOrg: false,
        isTeamMember: true,
        isSampleGuest: true,
        orgRole: CLERK_ORGANIZATION_ROLES.MEMBER,
      };
    }

    const isSfsAdmin = Boolean(has?.({ feature: "org:saferfoodscores" }) || isSfsUser);
    const orgRole = (membership?.role ?? sessionOrgRole ?? "").toString();

    const isAuditor = orgRole === CLERK_ORGANIZATION_ROLES.AUDITOR;
    const isConsultantManager = orgRole === CLERK_ORGANIZATION_ROLES.CONSULTANT_MANAGER;
    const isManager = orgRole === CLERK_ORGANIZATION_ROLES.MANAGER;
    // Owner is the org creator and maps to admin server-side; include it so
    // billing/trial gates match /admin/plans and settings tab visibility.
    const isClientAdmin =
      orgRole === CLERK_ORGANIZATION_ROLES.ADMIN ||
      orgRole === CLERK_ORGANIZATION_ROLES.CLIENT_ADMIN ||
      orgRole === CLERK_ORGANIZATION_ROLES.OWNER;
    const isSiteManager = orgRole === CLERK_ORGANIZATION_ROLES.SITE_MANAGER;
    const isAreaManager = orgRole === CLERK_ORGANIZATION_ROLES.AREA_MANAGER;
    const isTeamMember =
      orgRole === CLERK_ORGANIZATION_ROLES.MEMBER ||
      orgRole === CLERK_ORGANIZATION_ROLES.CLIENT_USER;
    const isClientMember =
      isTeamMember || orgRole === CLERK_ORGANIZATION_ROLES.VIEWER;
    const isClient = !isSfsAdmin && (isClientAdmin || isSiteManager || isAreaManager || isClientMember) && !isAuditor;

    const rawOrgType = organization?.publicMetadata?.orgType;
    const activeOrgType =
      typeof rawOrgType === "string" && isValidOrgType(rawOrgType) ? rawOrgType : null;
    // True when the user has an auditor role and their active org is a client org.
    // Used to grant auditors access to the client dashboard when operating in client context.
    const isAuditorInClientOrg = isAuditor && activeOrgType === ORG_TYPE.CLIENT;

    return {
      isSfsAdmin,
      isAuditor,
      isConsultantManager,
      isManager,
      isClient,
      isClientAdmin,
      isSiteManager,
      isAreaManager,
      isAuditorInClientOrg,
      /** ASA-1596: Team Member / client_user — Events raise + list only. */
      isTeamMember,
      isSampleGuest: false,
      orgRole: orgRole || undefined,
    };
  }, [
    has,
    isSampleGuest,
    isSfsUser,
    membership?.role,
    sessionOrgRole,
    organization?.publicMetadata?.orgType,
  ]);
}
